import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { AccountSubHeader } from '@corratech/pylot-account-header'
import { Maybe } from '@pylot-data/fwrdschema'
import cn from 'classnames'
import s from './MarketingConsent.module.scss'

interface MarketingConsentProps {
    title?: string
    subscriptionChannels?: Maybe<string>
    onSubscriptionsChange: (subscriptions: string[]) => void
    holderClass?: string
    isSignUp?: boolean
}

export const MarketingConsent: FC<MarketingConsentProps> = ({
    title,
    subscriptionChannels,
    onSubscriptionsChange,
    holderClass,
    isSignUp
}) => {
    const { t } = useTranslation(['common'])

    const [subscriptions, setSubscriptions] = useState(
        subscriptionChannels && subscriptionChannels.trim() !== ''
            ? subscriptionChannels.split(',').map((item) => item.trim())
            : []
    )

    const handleCheckboxChange = (key: string) => {
        setSubscriptions((prev) => {
            const updated = prev.includes(key)
                ? prev.filter((item) => item !== key)
                : [...prev, key]
            onSubscriptionsChange(updated)
            return updated
        })
    }

    const marketingConsentEntries = useMemo(
        () => [
            { id: 'email', label: t('account|Email') },
            { id: 'mail', label: t('account|Mail') },
            { id: 'phone', label: t('account|Phone') },
            {
                id: 'text_message',
                label: t('account|Text Message (SMS, WhatsApp)')
            }
        ],
        []
    )

    useEffect(() => {
        if (!isSignUp) return
        setSubscriptions(marketingConsentEntries.map((entry) => entry.id))
        onSubscriptionsChange(marketingConsentEntries.map((entry) => entry.id))
    }, [isSignUp, marketingConsentEntries])

    return (
        <div
            className={cn(
                s['marketing-consent-block'],
                'marketing-consent-block',
                holderClass && s[holderClass]
            )}
        >
            {title && <AccountSubHeader subHeading={title} />}
            <div className="form-group">
                <p>
                    {t(
                        'account|By selecting any of the communication options below, I agree to receive offers, promotions and other marketing and commercial messages from OOKA, its affiliates and/or distributors via the selected channel(s). By not selecting any option(s), I agree to only my personal data being saved.'
                    )}
                </p>
                <div
                    className={cn(
                        s['marketing-consent-checkbox-container'],
                        'marketing-consent-checkbox-container'
                    )}
                >
                    {marketingConsentEntries.map((entry) => (
                        <div
                            key={`consent-${entry.id}`}
                            className={cn(
                                'checkbox-container',
                                subscriptions?.includes(entry.id) && 'active'
                            )}
                        >
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id={`consent-${entry.id}`}
                                checked={subscriptions?.includes(entry.id)}
                                onChange={() => handleCheckboxChange(entry.id)}
                            />
                            <label
                                className="label"
                                htmlFor={`consent-${entry.id}`}
                            >
                                {entry.label}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
